/**
 * @voltxampere/web — File category builtin commands (Sprint 22 C3)
 *
 * 11 commands covering circuit file lifecycle: native save/save-as/
 * save-copy, .vxa export, .vxa import, URL hash share, new-empty,
 * new-from-template (= open the DEVRELER preset gallery), and three
 * recent-circuit slot placeholders that the C4 boot path replaces
 * with real recent entries from IndexedDB.
 *
 * Most actions delegate to existing Sprint 11-21 stores rather than
 * implementing new behaviour. The palette is a discovery surface, not
 * a re-implementation of the topbar / file menu.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Command } from "../types.js";
import { exportDialogOpen, importDialogOpen } from "../../store/import-export.js";
import { setPane } from "../../store/router.js";
import { currentCircuit } from "../../store/circuit.js";
import { canvasDemo } from "../../fixtures/canvas-demo.js";

const noop = (): void => {
  /* Sprint 22 stub — Sprint 23 polish wires actual save side effects. */
};

export const FILE_COMMANDS: readonly Command[] = [
  {
    id: "file.save",
    titleKey: "command.file.save",
    descriptionKey: "command.file.save.desc",
    category: "file",
    shortcut: "Cmd+S",
    execute: noop, // auto-save handles persistence; Cmd+S is a no-op affordance until S23 manual-save commit
  },
  // Sprint 80.Z Phase 4 D1 — `file.save-as` removed (rename-on-save
  // dialog not wired). Re-lands when the save-as flow ships.
  // `file.save-copy` is RETAINED with `when: () => false` because
  // future "Save as new circuit" flows are scoped for the next
  // round; the gate hides it from the palette today without
  // dropping the i18n token + Command shape (lighter churn on
  // re-enable than a full remove+restore cycle).
  {
    id: "file.save-copy",
    titleKey: "command.file.saveCopy",
    category: "file",
    when: () => false, // Sprint 80.Z Phase 4 D1 — gated until save-copy flow ships
    execute: noop,
  },
  {
    id: "file.export-vxa",
    titleKey: "command.file.exportVxa",
    descriptionKey: "command.file.exportVxa.desc",
    category: "file",
    shortcut: "Cmd+Shift+E",
    execute: () => {
      exportDialogOpen.value = true;
    },
  },
  {
    id: "file.import-vxa",
    titleKey: "command.file.importVxa",
    descriptionKey: "command.file.importVxa.desc",
    category: "file",
    shortcut: "Cmd+Shift+I",
    execute: () => {
      importDialogOpen.value = true;
    },
  },
  {
    id: "file.share-url",
    titleKey: "command.file.shareUrl",
    descriptionKey: "command.file.shareUrl.desc",
    category: "file",
    execute: () => {
      // The export dialog hosts the share-url tab; opening it
      // surfaces the same control without a parallel UI.
      exportDialogOpen.value = true;
    },
  },
  {
    id: "file.new-empty",
    titleKey: "command.file.newEmpty",
    descriptionKey: "command.file.newEmpty.desc",
    category: "file",
    execute: () => {
      // Reset to the canonical demo circuit; Sprint 23 may add a
      // truly-empty grid option behind a confirm dialog.
      currentCircuit.value = canvasDemo;
      setPane("simulator");
    },
  },
  {
    id: "file.new-from-template",
    titleKey: "command.file.newFromTemplate",
    descriptionKey: "command.file.newFromTemplate.desc",
    category: "file",
    execute: () => {
      setPane("presets");
    },
  },
  {
    id: "file.recent-1",
    titleKey: "command.file.recent.slot1",
    category: "file",
    when: () => false, // Sprint 22 C4 IndexedDB recent registers real slots
    execute: noop,
  },
  {
    id: "file.recent-2",
    titleKey: "command.file.recent.slot2",
    category: "file",
    when: () => false,
    execute: noop,
  },
  {
    id: "file.recent-3",
    titleKey: "command.file.recent.slot3",
    category: "file",
    when: () => false,
    execute: noop,
  },
];
